import React, { useState, useEffect } from "react";
import {
  Card,
  Divider,
  Tag,
  Row,
  Col,
  Typography,
  Descriptions,
  Spin,
  Button,
  Collapse,
  Image,
  Space,
  Badge,
  Timeline,
} from "antd";
import "./ProductBookingDetails.css";
import Navbar from "../../../layouts/navbar";
import Footer from "../../../layouts/footer";
import {
  ShoppingOutlined,
  CalendarOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ShopOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useProduct } from "./ProductContext";
import { useReferral } from "../../../utils/referral";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const ProductBookingDetails = observer((props: any) => {
  const { user } = useAuth();
  const { bookingId } = useParams();
  const { navigateWithReferral } = useReferral();
  const { fetchSingleProductBooking, singleProductBooking, loading } =
    useProduct();

  useEffect(() => {
    if (user && bookingId) {
      loadData();
    }
  }, [user, bookingId]);

  const loadData = async () => {
    await fetchSingleProductBooking({
      order_id: bookingId,
      UUID: user?.UUID,
    });
  };

  if (loading) {
    return (
      <div id="ProductBookingDetails">
        <Navbar hideBanner={true} />
        <div className="container">
          <div style={{ textAlign: "center", padding: "100px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Loading order details...</Text>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD MMM YYYY, HH:mm");
  };

  const formatDateOnly = (dateString: string) => {
    return moment(dateString).format("DD MMM YYYY");
  };

  const renderOrderStatus = (orderStatus: string, paymentStatus: string) => {
    const orderStatusLower = orderStatus?.toLowerCase();
    const paymentStatusLower = paymentStatus?.toLowerCase();

    if (orderStatusLower === "completed" && paymentStatusLower === "paid") {
      return <Badge count="Completed" style={{ backgroundColor: "#52c41a" }} />;
    } else if (
      orderStatusLower === "cancelled" ||
      paymentStatusLower === "failed"
    ) {
      return <Badge count="Cancelled" style={{ backgroundColor: "#ff4d4f" }} />;
    } else if (paymentStatusLower === "pending") {
      return (
        <Badge count="Pending Payment" style={{ backgroundColor: "#fa8c16" }} />
      );
    } else if (orderStatusLower === "processing") {
      return (
        <Badge count="Processing" style={{ backgroundColor: "#1890ff" }} />
      );
    } else {
      return (
        <Badge
          count={orderStatus || "Pending"}
          style={{ backgroundColor: "#722ed1" }}
        />
      );
    }
  };

  const getStatusIcon = (orderStatus: string, paymentStatus: string) => {
    const orderStatusLower = orderStatus?.toLowerCase();
    const paymentStatusLower = paymentStatus?.toLowerCase();

    if (orderStatusLower === "completed" && paymentStatusLower === "paid") {
      return (
        <CheckCircleOutlined
          style={{ color: "#52c41a" }}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      );
    } else if (
      orderStatusLower === "cancelled" ||
      paymentStatusLower === "failed"
    ) {
      return (
        <CloseCircleOutlined
          style={{ color: "#ff4d4f" }}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      );
    } else if (paymentStatusLower === "pending") {
      return (
        <ClockCircleOutlined
          style={{ color: "#fa8c16" }}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      );
    } else {
      return (
        <ClockCircleOutlined
          style={{ color: "#1890ff" }}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      );
    }
  };

  const calculateTotalItems = (productDetail: any[]) => {
    return productDetail?.reduce(
      (total, product) => total + parseInt(product.params.qty),
      0
    );
  };

  const getUniquePickupDates = (productDetail: any[]) => {
    const dates = productDetail.map((product) => product.pickupdate);
    return [...new Set(dates)].sort();
  };

  const groupProductsByPickupDate = (productDetail: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    productDetail?.forEach((product) => {
      if (!grouped[product.pickupdate]) {
        grouped[product.pickupdate] = [];
      }
      grouped[product.pickupdate].push(product);
    });
    return grouped;
  };

  if (!singleProductBooking) {
    return (
      <div id="ProductBookingDetails">
        <Navbar hideBanner={true} />
        <div className="container">
          <div style={{ textAlign: "center", padding: "100px" }}>
            <ShoppingOutlined
              style={{ fontSize: "64px", color: "#ddd", marginBottom: "16px" }}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <Title level={3} type="secondary">
              Order Not Found
            </Title>
            <Text type="secondary">
              The requested order could not be found.
            </Text>
            <br />
            <Button
              type="primary"
              style={{ marginTop: 16 }}
              onClick={() => navigateWithReferral("/bookings/product")}
            >
              Back to Orders
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const groupedProducts = groupProductsByPickupDate(
    singleProductBooking.product_detail
  );
  const totalItems = calculateTotalItems(singleProductBooking.product_detail);

  return (
    <div id="ProductBookingDetails">
      <div className="container">
        <Navbar hideBanner={true} />
        {/* Header Section */}
        <div className="booking-header">
          <Button
            type="link"
            onClick={() => navigateWithReferral("/bookings/product")}
            style={{ padding: 0, marginBottom: 16 }}
          >
            ← Back to Orders
          </Button>

          <div className="header-main">
            <div className="header-left">
              <Title level={2} style={{ margin: 0 }}>
                Order #{singleProductBooking.order_id}
              </Title>
              <Text type="secondary">
                Placed on {formatDate(singleProductBooking.order_date)}
              </Text>
            </div>
            <div className="header-right">
              {renderOrderStatus(
                singleProductBooking.order_status,
                singleProductBooking.payment_status
              )}
            </div>
          </div>
        </div>

        <Divider />

        <Row
          gutter={[16, 16]}
          style={{ marginBottom: 24 }}
          className="summary-grid"
        >
          {/* <Col xs={12} sm={12} md={8}>
            <Card size="small" className="summary-card">
              <div className="summary-item">
                <UserOutlined
                  className="summary-icon"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <div>
                  <Text strong>Customer</Text>
                  <br />
                  <Text>
                    {singleProductBooking.firstName}{" "}
                    {singleProductBooking.lastname}
                  </Text>
                </div>
              </div>
            </Card>
          </Col> */}
          <Col xs={12} sm={12} md={8}>
            <Card size="small" className="summary-card">
              <div className="summary-item">
                <ShopOutlined
                  className="summary-icon"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <div>
                  <Text strong>Restaurant</Text>
                  <br />
                  <Text>{singleProductBooking.order_from}</Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8}>
            <Card size="small" className="summary-card">
              <div className="summary-item">
                <ShoppingOutlined
                  className="summary-icon"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <div>
                  <Text strong>Total Items</Text>
                  <br />
                  <Text>{totalItems} items</Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8}>
            <Card size="small" className="summary-card">
              <div className="summary-item">
                <DollarOutlined
                  className="summary-icon"
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <div>
                  <Text strong>Total Amount</Text>
                  <br />
                  <Text strong style={{ color: "#ff4d4f", fontSize: "16px" }}>
                    RM{" "}
                    {parseFloat(singleProductBooking.Total_amount).toFixed(2)}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Replace the Order Details section with this: */}
        <Row gutter={[0, 24]}>
          <Col xs={24}>
            <div className="compact-layout">
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  {/* Combined Order & Customer Information */}
                  <Card
                    title={
                      <Space>
                        <FileTextOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                        Order & Customer Information
                      </Space>
                    }
                    className="info-card compact-card"
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        {/* Order Information Section */}
                        <div style={{ marginBottom: 16 }}>
                          <Text
                            strong
                            style={{
                              fontSize: "16px",
                              marginBottom: 8,
                              display: "block",
                            }}
                          >
                            Order Details
                          </Text>
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="Order ID">
                              <Text copyable strong>
                                #{singleProductBooking.order_id}
                              </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Date">
                              {formatDate(singleProductBooking.order_date)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Type">
                              <Tag color="blue">
                                {singleProductBooking.order_type}
                              </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Status">
                              <Space>
                                {getStatusIcon(
                                  singleProductBooking.order_status,
                                  singleProductBooking.payment_status
                                )}
                                {singleProductBooking.order_status}
                              </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Payment Status">
                              <Tag
                                color={
                                  singleProductBooking.payment_status === "paid"
                                    ? "green"
                                    : "orange"
                                }
                              >
                                {singleProductBooking.payment_status ||
                                  "Pending"}
                              </Tag>
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      </Col>

                      <Col xs={24} md={12}>
                        {/* Customer Information Section */}
                        <div style={{ marginBottom: 16 }}>
                          <Text
                            strong
                            style={{
                              fontSize: "16px",
                              marginBottom: 8,
                              display: "block",
                            }}
                          >
                            Customer Details
                          </Text>
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="Name">
                              {singleProductBooking.firstName}{" "}
                              {singleProductBooking.lastname}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                              <Space>
                                <PhoneOutlined
                                  onPointerEnterCapture={undefined}
                                  onPointerLeaveCapture={undefined}
                                />
                                {singleProductBooking.phone}
                              </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                              <Space>
                                <MailOutlined
                                  onPointerEnterCapture={undefined}
                                  onPointerLeaveCapture={undefined}
                                />
                                {singleProductBooking.email}
                              </Space>
                            </Descriptions.Item>
                          </Descriptions>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </div>
          </Col>

          <Col xs={24}>
            {/* Products by Pickup Date */}
            <Card
              title={
                <Space>
                  <CalendarOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                  Order Items by Pickup Date
                </Space>
              }
              className="products-card"
            >
              {Object.entries(groupedProducts).map(([pickupDate, products]) => (
                <div key={pickupDate} className="pickup-group">
                  <div className="pickup-header">
                    <Text strong style={{ fontSize: "16px" }}>
                      Pickup: {formatDateOnly(pickupDate)}
                    </Text>
                  </div>

                  {products.map((product, index) => (
                    <div key={index} className="product-item">
                      <div className="product-main">
                        <div className="product-info">
                          <Text strong style={{ fontSize: "16px" }}>
                            {product.title}
                          </Text>
                          <br />
                          <Text type="secondary">
                            Pickup Time: {product.pickuptime}
                          </Text>
                          <br />
                          <Text type="secondary">
                            Quantity: {product.params.qty}
                          </Text>

                          {product.params.product_variant &&
                            product.params.product_variant.length > 0 && (
                              <div className="variants-section">
                                <Text
                                  type="secondary"
                                  style={{ display: "block", marginTop: 8 }}
                                >
                                  Variants:
                                </Text>
                                {product.params.product_variant.map(
                                  (variant: any, vIndex: number) => (
                                    <Tag
                                      key={vIndex}
                                      color="blue"
                                      style={{ marginTop: 4 }}
                                    >
                                      {variant.variation_title}: {variant.title}
                                      {variant.price !== "0.00" &&
                                        ` (+RM ${parseFloat(
                                          variant.price
                                        ).toFixed(2)})`}
                                    </Tag>
                                  )
                                )}
                              </div>
                            )}

                          {product.params.remark && (
                            <div className="product-remark">
                              <Text type="secondary">
                                Note: {product.params.remark}
                              </Text>
                            </div>
                          )}
                        </div>

                        <div className="product-price">
                          <Text
                            strong
                            style={{ fontSize: "16px", color: "#ff4d4f" }}
                          >
                            RM {product.params.total_price.toFixed(2)}
                          </Text>
                          <br />
                          <Text type="secondary">
                            RM {parseFloat(product.prices.price).toFixed(2)}{" "}
                            each
                          </Text>
                        </div>
                      </div>

                      {product.description && (
                        // @ts-ignore
                        <Collapse size="small" style={{ marginTop: 12 }}>
                          {/* @ts-ignore */}
                          <Panel header="Product Description" key="description">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: product.description,
                              }}
                              className="product-description"
                            />
                          </Panel>
                        </Collapse>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          </Col>
        </Row>

        {/* Order Timeline */}
        {/* <Card
          title="Order Timeline"
          style={{ marginTop: 24, marginBottom: 24 }}
          className="timeline-card"
        >
          
          <Timeline>
            
            <Timeline.Item
              color="green"
              dot={
                <CheckCircleOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
            >
              <Text strong>Order Placed</Text>
              <br />
              <Text type="secondary">
                {formatDate(singleProductBooking.order_date)}
              </Text>
            </Timeline.Item>
            
            <Timeline.Item
              color={
                singleProductBooking.payment_status === "paid"
                  ? "green"
                  : "blue"
              }
              dot={
                singleProductBooking.payment_status === "paid" ? (
                  <CheckCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                ) : (
                  <ClockCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                )
              }
            >
              <Text strong>
                Payment{" "}
                {singleProductBooking.payment_status === "paid"
                  ? "Completed"
                  : "Pending"}
              </Text>
              <br />
              <Text type="secondary">
                {singleProductBooking.payment_status === "paid"
                  ? "Payment received"
                  : "Awaiting payment"}
              </Text>
            </Timeline.Item>
            
            <Timeline.Item
              color={
                singleProductBooking.order_status === "completed"
                  ? "green"
                  : "blue"
              }
              dot={
                singleProductBooking.order_status === "completed" ? (
                  <CheckCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                ) : (
                  <ClockCircleOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                )
              }
            >
              <Text strong>
                Order{" "}
                {singleProductBooking.order_status === "completed"
                  ? "Completed"
                  : "Processing"}
              </Text>
              <br />
              <Text type="secondary">
                {singleProductBooking.order_status === "completed"
                  ? "Order fulfilled"
                  : "Preparing your order"}
              </Text>
            </Timeline.Item>
          </Timeline>
        </Card> */}
        <Footer />
      </div>
    </div>
  );
});

export default ProductBookingDetails;
